import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';
import { Card, CardHeader } from '@/components/atoms/Card';
import { LastMeasurement } from '@/components/atoms/LastMeasurement/LastMeasurement';
import { LastPlacement } from '@/components/atoms/LastPlacement/LastPlacement';
import { Spinner } from '@/components/atoms/Spinner';
import StatusDot from '@/components/atoms/StatusDot';
import { BioscopeConfiguration } from '@/components/molecules/BioscopeConfiguration/BioscopeConfiguration';
import { BioscopeConnection } from '@/components/molecules/BioscopeConnection/BioscopeConnection';
import { BioscopeMeta } from '@/components/molecules/BioscopeMeta/BioscopeMeta';
import { BioscopePosition } from '@/components/molecules/BioscopePosition/BioscopePosition';
import BioscopeRestarts, {
    type BioscopeRestartsProps,
} from '@/components/molecules/BioscopeRestarts/BioscopeRestarts';
import { BioscopeSystemReadings } from '@/components/molecules/BioscopeSystemReadings/BioscopeSystemReadings';
import { BioscopeTestSettings } from '@/components/molecules/BioscopeTestSettings';
import { confirm } from '@/components/molecules/ConfirmDialog';
import { CurrentBioscopePlacement } from '@/components/molecules/CurrentBioscopePlacement/CurrentBioscopePlacement';
import { EmptyState } from '@/components/molecules/EmptyState';
import { decodeMqttText } from '@/contexts/mqtt/payload';
import { useMqtt } from '@/contexts/mqtt-provider-context';
import type { BioscopeRestart, BioscopeRouterRestart } from '@/domain/bioscopeRestarts';
import type { BioscopeDetail as BioscopeDetailTypes } from '@/domain/bioscopes';
import { BioscopeImageCardContainer } from '@/routes/Client/BioscopeDetail/BioscopeImageCardContainer';
import isObject from '@/utils/isObject';

import { showLuxReply } from './luxReplyToast';

import styles from './BioscopeDetail.module.scss';

interface BioscopeDetailProps {
    isBioscopeDetailLoading: boolean;
    isOptoScaleAdmin: boolean;
    /** Undefined until the detail has loaded. */
    bioscope: BioscopeDetailTypes | undefined;

    deleteLastBioscopeMove?: () => Promise<unknown>;
    /** Runs inside the confirmation, which waits for it and shows a failure. */
    deleteBioscope?: () => Promise<unknown>;
    moveBioscope?: () => void;
    bioscopeRestarts: BioscopeRestartsProps<BioscopeRestart>;
    bioscopeRouterRestarts: BioscopeRestartsProps<BioscopeRouterRestart>;
    onUpdateTestBioscope?: (isTestBioscope: boolean, description: string | null) => Promise<void>;
    isUpdatingTestBioscope?: boolean;
    /** Names the client of each placement. The internal bioscope page sets this. */
    showPlacementClient?: boolean;

    queryTypes?: {
        CAGE?: string;
        BIOSCOPE?: string;
        UNCLASSIFIED?: string;
    };
}

function BioscopeDetail({
    bioscopeRestarts,
    bioscopeRouterRestarts,
    isBioscopeDetailLoading,
    bioscope,
    deleteLastBioscopeMove,
    deleteBioscope,
    isOptoScaleAdmin,
    moveBioscope,
    onUpdateTestBioscope,
    isUpdatingTestBioscope,
    showPlacementClient = false,
    queryTypes,
}: BioscopeDetailProps) {
    const { t } = useTranslation();

    const { client, isConnected, subscribe, publish } = useMqtt();
    const [bioscopeData, setbioscopeData] = useState<Record<string, unknown> | null>(null);
    const [configData, setConfigData] = useState<Record<string, unknown> | null>(null);
    const [isOnline, setIsOnline] = useState<boolean>(false);

    const messageHandler = useCallback(
        (topic: string, message: Uint8Array) => {
            if (topic.includes('online')) {
                const byte = message[0];
                setIsOnline(byte === 1 || byte === 0x31); // 0x01 (binary) or '1' (ASCII 49)
                return;
            }

            try {
                const parsedMessage: unknown = JSON.parse(decodeMqttText(message));
                console.log(`Received message from ${topic}:`, parsedMessage);
                const fields = isObject(parsedMessage) ? parsedMessage : null;
                if (topic.includes('data')) {
                    setbioscopeData(fields);
                } else if (topic.includes('config')) {
                    setConfigData(fields);
                } else if (topic.includes('lux')) {
                    const status = fields?.status;
                    if (typeof status !== 'string') {
                        throw new Error('Lux response has no status');
                    }
                    console.log(`Received lux message: ${status}`);
                    const toastMessage = t('bioscopeDetail.systemReadings.luxResponse', {
                        status,
                        message: fields?.message,
                    });
                    showLuxReply(toastMessage, status.toLowerCase() === 'success');
                }
            } catch (error) {
                console.error(`Error parsing message from ${topic}:`, error);
            }
        },
        [t]
    );

    useEffect(() => {
        if (isConnected && bioscope?.id) {
            const topics = [
                `bioscope/data/response/${bioscope.id}`,
                `bioscope/config/response/${bioscope.id}`,
                `bioscope/set/lux/response/${bioscope.id}`,
                `v1/bioscope/${bioscope.id}/online`,
            ];

            for (const topic of topics) {
                subscribe(topic, { qos: 1 });
                console.log(`Subscribed to topic: ${topic}`);
            }

            client?.on('message', messageHandler);

            return () => {
                client?.off('message', messageHandler);
            };
        }
    }, [isConnected, bioscope?.id, subscribe, client, messageHandler]);

    const handlePublish = useCallback(
        (topic: string, message: string) => {
            if (client && isConnected) {
                publish(topic, message, { qos: 1 });
            }
        },
        [client, isConnected]
    );

    // After every hook: returning earlier changes the hook count between renders.
    if (isBioscopeDetailLoading) {
        return <Spinner size="lg" fullScreen />;
    }

    if (!bioscope) {
        return <EmptyState title={t('common.labels.noData')} />;
    }

    return (
        <div className={styles.bioscopeDetail}>
            <h2>
                {t('common.labels.bioscopeStatus')}:{' '}
                <StatusDot tone={isOnline ? 'success' : 'danger'}>
                    {isOnline ? t('common.labels.online') : t('common.labels.offline')}
                </StatusDot>
            </h2>

            <div className={styles.bioscopeDetailPanelWrapper}>
                <CurrentBioscopePlacement
                    loading={false}
                    error={false}
                    errorText={t('bioscopeDetail.placement.updateError')}
                    title="bioscopeDetail.placement.title"
                    client={bioscope.client}
                    deleteLastBioscopeMove={deleteLastBioscopeMove}
                    moveBioscope={moveBioscope}
                />
                <LastPlacement
                    title="organisms.bioscopeDetail.lastPlacement"
                    lastLocation={bioscope.lastLocation}
                />
                <LastMeasurement lastMeasurement={bioscope.lastMeasurement} />
            </div>
            <div className={styles.bioscopeDetailPanelWrapper}>
                <BioscopeMeta
                    bioscopeMetaData={bioscope.version}
                    isLoading={isBioscopeDetailLoading}
                />
                {queryTypes && (
                    <div className={styles.BioscopeImageCardContainer}>
                        <BioscopeImageCardContainer bioscope={bioscope} />
                    </div>
                )}
            </div>

            {isOptoScaleAdmin && (
                <div className={styles.bioscopeDetailPanelWrapper}>
                    <BioscopeConfiguration
                        bioscope={bioscope}
                        configData={configData}
                        handlePublish={handlePublish}
                    />
                    <BioscopeSystemReadings
                        bioscope={bioscope}
                        bioscopeData={bioscopeData}
                        handlePublish={handlePublish}
                    />
                    {onUpdateTestBioscope && (
                        <BioscopeTestSettings
                            isTestBioscope={bioscope.isTestBioscope}
                            description={bioscope.description}
                            onSave={onUpdateTestBioscope}
                            isLoading={isUpdatingTestBioscope}
                        />
                    )}
                </div>
            )}
            <div className={styles.bioscopeDetailPanelWrapperFullWidth}>
                <BioscopeConnection connection={bioscope.connection} />
            </div>
            <div className={styles.bioscopeDetailPanelWrapperFullWidth}>
                <Card
                    as="section"
                    padding="xl"
                    header={
                        <CardHeader title={t('bioscopeDetail.restarts.bioscope')} level="h2" />
                    }>
                    <BioscopeRestarts {...bioscopeRestarts} />
                </Card>
            </div>
            <div className={styles.bioscopeDetailPanelWrapperFullWidth}>
                <Card
                    as="section"
                    padding="xl"
                    header={<CardHeader title={t('bioscopeDetail.restarts.router')} level="h2" />}>
                    <BioscopeRestarts {...bioscopeRouterRestarts} />
                </Card>
            </div>

            <div className={styles.bioscopeDetailPanelWrapperFullWidth}>
                <BioscopePosition
                    bioscopeLocations={bioscope.locations}
                    showClient={showPlacementClient}
                />
            </div>
            <div className={styles.bioscopeDetailPanelWrapperFullWidth}>
                <Card>
                    <h3>{t('bioscopeDetail.delete.title')}</h3>
                    <Button
                        variant="danger"
                        name="bioscope_delete"
                        trackingId="bioscope_delete"
                        onClick={() =>
                            deleteBioscope &&
                            confirm({
                                title: t('bioscopeDetail.delete.title'),
                                message: t('bioscopeDetail.delete.confirm', { id: bioscope.id }),
                                confirmLabel: t('common.actions.delete'),
                                errorFallback: t('bioscopeDetail.delete.error'),
                                onConfirm: deleteBioscope,
                            })
                        }>
                        {t('common.actions.deleteEntry')}
                    </Button>
                </Card>
            </div>
        </div>
    );
}

export default BioscopeDetail;
export { BioscopeDetail };
